import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE, resolveImageUrl } from "../../config/api";
import { useNavigate } from "react-router-dom";
import Pagination from "../../Components/Pagination";
import { usePosSettings } from "../../utils/posSettings";

interface Product {
  product_id: number;
  product_name: string;
  category_id: number | null;
  brand_id: number | null;
  quantity: number;
  price: number;
  product_image: string | null;
  product_image_url: string | null;
}

interface Category { category_id: number; category_name: string; }
interface Brand { brand_id: number; brand_name: string; }

const THRESHOLD_OPTIONS = [0, 5, 10, 20, 50];

const LowStockPage = () => {
  const settings = usePosSettings();
  const pageSize = settings.itemsPerPage || 25;
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [threshold, setThreshold] = useState(settings.lowStockDefault || 10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [outOfStockTotal, setOutOfStockTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [cookies] = useCookies();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [threshold, debouncedSearchTerm]);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ maxQuantity: threshold.toString(), page: page.toString(), limit: pageSize.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/product?${params.toString()}`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setProducts(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching low stock products:", error);
    } finally {
      setIsLoading(false);
    }
  }, [threshold, page, pageSize, debouncedSearchTerm, cookies.auth]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // "Out of stock" is a fixed count (quantity === 0) independent of the
  // selected threshold and of which page is currently loaded — it needs its
  // own query against the server's total, not a count over the current
  // page's 25 rows (which understates it whenever out-of-stock products are
  // spread across more than one page).
  const fetchOutOfStockTotal = useCallback(async () => {
    try {
      const params = new URLSearchParams({ maxQuantity: "0", page: "1", limit: "1" });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/product?${params.toString()}`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setOutOfStockTotal(data?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching out-of-stock total:", error);
    }
  }, [debouncedSearchTerm, cookies.auth]);

  useEffect(() => {
    fetchOutOfStockTotal();
  }, [fetchOutOfStockTotal]);

  const fetchMeta = useCallback(async () => {
    try {
      const [catRes, brandRes] = await Promise.all([
        authenticatedFetch(`${API_BASE}/category?page=1&limit=100`, { method: "GET" }, cookies.auth),
        authenticatedFetch(`${API_BASE}/brand?page=1&limit=100`, { method: "GET" }, cookies.auth),
      ]);
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(Array.isArray(catData?.data) ? catData.data : []);
      }
      if (brandRes.ok) {
        const brandData = await brandRes.json();
        setBrands(Array.isArray(brandData?.data) ? brandData.data : []);
      }
    } catch (error) {
      console.error("Error fetching meta:", error);
    }
  }, [cookies.auth]);

  const getCategoryName = (id: number | null) => {
    if (!id) return "—";
    return categories.find(c => c.category_id === id)?.category_name || `Cat ${id}`;
  };

  const getBrandName = (id: number | null) => {
    if (!id) return "—";
    return brands.find(b => b.brand_id === id)?.brand_name || `Brand ${id}`;
  };

  const filtered = [...products].sort((a, b) => (a.quantity ?? 0) - (b.quantity ?? 0));

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Low Stock</h1>
        <div className={style.controls}>
          <input
            type="text"
            placeholder="Search by product name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          <div className={style.thresholdGroup}>
            <label className={style.thresholdLabel}>Show quantity ≤</label>
            <select value={threshold} onChange={e => setThreshold(parseInt(e.target.value))} className={style.thresholdSelect}>
              {THRESHOLD_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button className={style.restockBtn} onClick={() => navigate("/inventory")}>
            + Restock via Inventory
          </button>
        </div>
      </div>

      <div className={style.statsBar}>
        <span className={style.statChip}>Showing: <strong>{totalItems}</strong> products</span>
        {outOfStockTotal > 0 && <span className={style.statChipDanger}>Out of Stock: <strong>{outOfStockTotal}</strong></span>}
      </div>

      <div className={style.tableContainer}>
        {isLoading ? (
          <div className={style.loading}>Loading...</div>
        ) : (
          <table className={style.dataTable}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Image</th>
                <th>Product Name</th>
                <th>Category</th>
                <th>Brand</th>
                <th>Quantity</th>
                <th>Price (PKR)</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.product_id} className={style.tableRow}>
                  <td>{p.product_id}</td>
                  <td>
                    {p.product_image_url ? (
                      <img src={resolveImageUrl(p.product_image_url)} alt={p.product_name} className={style.productThumb} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : <span className={style.noImage}>—</span>}
                  </td>
                  <td className={style.productName}>{p.product_name}</td>
                  <td>{getCategoryName(p.category_id)}</td>
                  <td>{getBrandName(p.brand_id)}</td>
                  <td>
                    <span className={(p.quantity ?? 0) === 0 ? style.qtyZero : style.qtyLow}>
                      {p.quantity ?? 0}
                    </span>
                  </td>
                  <td>{Number(p.price).toFixed(2)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className={style.emptyState}>
                  {isLoading ? "Loading..." : `No products with quantity ≤ ${threshold}.`}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />
    </div>
  );
};

export default LowStockPage;
